import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@wehunt/core-web'
import { listTeamPinFolders, type TeamPinFolderDto } from './api/teamPinFoldersApi'

export interface GroupPinFolders {
  folders: TeamPinFolderDto[]
  /**
   * False only while the answer is still unknown — mirrors `isResolved` on
   * {@link useGroupMapLines}, and exists for the same reason: MapEditorRoute
   * both hands these folders to the editor AND later diffs its save against
   * them (`saveTeamMap`'s `folders.original`), and an empty `folders` from an
   * unresolved query is indistinguishable from "this team has no folders". The
   * editor snapshots `team.map` once per mount with no prop-sync, so mounting
   * it before this query answers would freeze `original` at `[]` — and a save
   * from that editor would then read every one of the team's real folders as
   * deleted and DELETE them (see the CLUB-528 comment above `if
   * (!linesResolved) return null` in MapEditorRoute for the identical failure
   * mode on lines).
   *
   * A failed query counts as resolved: `folders` is then empty on both sides
   * of that diff, which produces no folder writes at all, rather than
   * stranding the editor behind a spinner forever.
   */
  isResolved: boolean
}

/**
 * The team's map-pin folders, in the order the collection stores them.
 *
 * Deliberately a separate query from {@link useGroupMapPins} rather than a field
 * on it: the folders live on the group document while the pins are their own
 * collection, so they are two reads either way, and a surface that only draws
 * sprites on a map (the hunt planner's stand picker, the print view) has no
 * reason to fetch folder names it will not show.
 *
 * `folders` reads as an empty list while loading and for a team with no
 * folders — a caller merely grouping pins by folder (not diffing against this
 * result) treats both the same way: everything at the root. A caller that DOES
 * diff (MapEditorRoute) must additionally gate on `isResolved`.
 */
/**
 * One shared empty list, so a query that has not answered yet returns the SAME
 * value every render. A fresh `[]` each time changes the identity of everything
 * built from it — and the team map view re-frames the whole ground whenever the
 * map object it is handed changes identity, which is what reset a reader's zoom
 * on every unrelated re-render.
 */
const NO_FOLDERS: TeamPinFolderDto[] = []

export function useGroupPinFolders(groupId: string): GroupPinFolders {
  const { state } = useAuth()
  const isSignedIn = state.status === 'signed-in'
  const enabled = isSignedIn && !!groupId

  const { data, status } = useQuery({
    queryKey: ['clubhouse', 'teams', groupId, 'map-pin-folders'],
    enabled,
    queryFn: async () => {
      const token = await (state as Extract<typeof state, { status: 'signed-in' }>).user.getIdToken()
      return listTeamPinFolders(groupId, token)
    },
  })

  return {
    folders: data ?? NO_FOLDERS,
    isResolved: !enabled || status !== 'pending',
  }
}
